<div class="tw-flex tw-flex-col tw-gap-4 tw-pb-4">
  @if (!org.isOwner) {
    <p class="tw-text-sm tw-text-dt-text-secondary">{{ "youAreMemberOfTeam" | i18n }}</p>
  } @else {
    <!-- MEMBERS -->
    <div class="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-bg-glass-card tw-p-4">
      <span
        class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-dt-text-tertiary"
        >{{ "membersSectionTitle" | i18n }} · {{ memberCount }}</span
      >

      @if (isLoading && memberRows.length === 0) {
        <p class="tw-text-sm tw-text-neutral-400">{{ "loading" | i18n }}</p>
      }
      @if (hasLoadError) {
        <div class="tw-flex tw-items-center tw-justify-between">
          <p class="tw-text-sm tw-text-dt-text-warning">{{ "errorOccurred" | i18n }}</p>
          <button
            type="button"
            class="tw-text-sm tw-font-medium tw-text-teal-600 tw-underline"
            (click)="retryLoadMembers()"
          >
            {{ "retry" | i18n }}
          </button>
        </div>
      }

      @for (row of memberRows; track row.id) {
        <div
          class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3"
          [ngClass]="row.isOwner ? 'tw-rounded-xl tw-bg-color-surface-sunken tw-px-4 tw-py-3' : ''"
        >
          <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-3">
            <div
              class="tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-color-surface-brand tw-text-sm tw-font-semibold tw-text-color-intent-brand"
            >
              {{ (row.name || row.email).charAt(0).toUpperCase() }}
            </div>
            <div class="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
              <span
                class="tw-truncate tw-font-montserrat tw-text-[13px] tw-font-semibold tw-leading-none tw-text-color-text-default"
                >{{ row.name || row.email }}</span
              >
              <span
                class="tw-truncate tw-font-montserrat tw-text-[11px] tw-font-medium tw-leading-none tw-text-color-text-tertiary"
                >{{ row.subtitle }}</span
              >
            </div>
          </div>

          <div class="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
            @if (row.actionButton) {
              @if (row.actionBadge) {
                <span [ngClass]="row.actionBadge.cssClass">{{
                  row.actionBadge.labelKey | i18n
                }}</span>
              }
              <app-gloss-button
                [options]="row.actionButton"
                [fontClass]="row.actionButtonFontClass"
              ></app-gloss-button>
            } @else if (row.badgeKey) {
              <span [ngClass]="badgeClass[row.badgeKey]">{{
                badgeLabelKey[row.badgeKey] | i18n
              }}</span>
            }

            @if (row.menuItems.length > 0) {
              <button
                type="button"
                cdkOverlayOrigin
                #trigger="cdkOverlayOrigin"
                class="tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-color-surface-hover"
                [attr.aria-label]="'moreActions' | i18n"
                (click)="row.menuOpen = !row.menuOpen"
              >
                <svg class="tw-h-4 tw-w-4 tw-text-dt-text-tertiary">
                  <use xlink:href="images/sprite.svg#more-horizontal"></use>
                </svg>
              </button>
              <ng-template
                cdkConnectedOverlay
                [cdkConnectedOverlayOrigin]="trigger"
                [cdkConnectedOverlayOpen]="row.menuOpen"
                [cdkConnectedOverlayPositions]="menuPositions"
                cdkConnectedOverlayHasBackdrop
                cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
                (backdropClick)="row.menuOpen = false"
                (detach)="row.menuOpen = false"
              >
                <div
                  class="tw-flex tw-w-48 tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-color-surface-card tw-py-1 tw-shadow-elevation-card"
                >
                  @for (item of row.menuItems; track item.label) {
                    <button
                      type="button"
                      class="tw-px-4 tw-py-2 tw-text-left tw-text-sm hover:tw-bg-color-surface-hover disabled:tw-opacity-40"
                      [ngClass]="
                        item.danger ? 'tw-text-color-status-error-text' : 'tw-text-dt-text-headings'
                      "
                      [disabled]="row.isBusy"
                      (click)="item.onClick(); row.menuOpen = false"
                    >
                      {{ item.label }}
                    </button>
                  }
                </div>
              </ng-template>
            }
          </div>
        </div>
      }

      @if (!addingMember) {
        <button
          type="button"
          class="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-2 tw-rounded-xl tw-bg-color-surface-sunken tw-px-4 tw-py-3 tw-font-montserrat tw-text-sm tw-font-semibold tw-text-color-intent-brand"
          (click)="toggleAddMember()"
        >
          <svg class="tw-h-3 tw-w-3 tw-text-color-intent-brand">
            <use xlink:href="images/sprite.svg#add"></use>
          </svg>
          {{ "addMember" | i18n }}
        </button>
      } @else {
        <div
          class="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-color-surface-sunken tw-px-4 tw-py-3"
        >
          <div class="tw-flex tw-items-center tw-gap-2">
            <input
              type="email"
              class="tw-w-full tw-flex-1 tw-rounded-xl tw-border tw-border-transparent tw-bg-color-surface-input tw-px-3.5 tw-py-3 tw-font-montserrat tw-text-sm tw-font-medium tw-leading-none tw-text-color-text-default tw-shadow-elevation-input placeholder:tw-text-color-text-tertiary focus:tw-border-[1.5px] focus:tw-border-color-border-action focus:tw-shadow-elevation-input-focus focus:tw-outline-none"
              [ngClass]="addMemberError ? '!tw-border-color-status-error' : ''"
              [placeholder]="'memberEmailPlaceholder' | i18n"
              [(ngModel)]="newMemberEmail"
              (keydown.enter)="submitAddMember()"
              [disabled]="isInviting"
            />
            <button
              type="button"
              class="tw-h-10 tw-w-[80px] tw-shrink-0 tw-rounded-xl tw-bg-color-action-primary tw-text-sm tw-font-semibold tw-text-color-text-on-action disabled:tw-opacity-40"
              [disabled]="isInviting || !newMemberEmail.trim()"
              (click)="submitAddMember()"
            >
              {{ "add" | i18n }}
            </button>
          </div>
          <span
            class="tw-font-montserrat tw-text-[11px] tw-font-medium tw-leading-none tw-text-color-text-tertiary"
            >{{ "addMemberHelperText" | i18n }}</span
          >
        </div>
      }
    </div>

    <!-- SHARED ACCOUNTS -->
    <div class="tw-flex tw-flex-col tw-gap-2 tw-rounded-2xl tw-bg-glass-card tw-p-4">
      <span
        class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-dt-text-tertiary"
        >{{ "sharedAccounts" | i18n }} · {{ sharedAccounts.length }}</span
      >
      @if (sharedAccounts.length === 0) {
        <p class="tw-text-sm tw-text-dt-text-tertiary">
          {{ "nothingSharedYet" | i18n: collectionName }}
        </p>
      } @else {
        <div class="tw-flex tw-flex-col tw-gap-3">
          @for (account of sharedAccounts; track account.id) {
            <button
              type="button"
              class="tw-flex tw-items-center tw-justify-between tw-rounded-xl tw-bg-color-surface-sunken tw-px-4 tw-py-3 tw-text-left"
              (click)="goToAccounts(account.id)"
            >
              <span class="tw-truncate tw-text-sm tw-font-medium tw-text-dt-text-headings">{{
                account.name
              }}</span>
              <svg class="tw-h-4 tw-w-4 tw-shrink-0 tw-text-color-icon-default">
                <use xlink:href="images/sprite.svg#right-arrow"></use>
              </svg>
            </button>
          }
        </div>
        <p class="tw-text-xs tw-text-dt-text-tertiary">
          {{ "sharedAccountsReadOnlyNote" | i18n: collectionName }}
        </p>
      }
    </div>

    <!-- TEAM -->
    <div class="tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-bg-glass-card tw-p-4">
      <span
        class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-dt-text-tertiary"
        >{{ "team" | i18n }}</span
      >
      <label class="tw-flex tw-flex-col tw-gap-1">
        <span
          class="tw-font-montserrat tw-text-[11px] tw-font-medium tw-leading-none tw-text-color-text-tertiary"
          >{{ "teamName" | i18n }}</span
        >
        <input
          type="text"
          maxlength="50"
          class="tw-w-full tw-rounded-xl tw-border tw-border-transparent tw-bg-color-surface-input tw-px-3.5 tw-py-3 tw-font-montserrat tw-text-[13px] tw-font-medium tw-leading-none tw-text-color-text-default tw-shadow-elevation-input focus:tw-border-[1.5px] focus:tw-border-color-border-action focus:tw-shadow-elevation-input-focus focus:tw-outline-none"
          [(ngModel)]="teamName"
          [disabled]="isRenaming"
          (blur)="onTeamNameBlur()"
        />
      </label>

      <div
        class="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-xl tw-border tw-border-color-status-error-border tw-bg-color-status-error-bg tw-py-3 tw-px-3.5"
      >
        <div class="tw-flex tw-flex-col tw-gap-1">
          <span
            class="tw-font-montserrat tw-text-[13px] tw-font-semibold tw-leading-none tw-text-color-status-error-text"
            >{{ "deleteThisTeam" | i18n }}</span
          >
          <span
            class="tw-font-montserrat tw-text-[11px] tw-font-medium tw-leading-none tw-text-color-status-error-text"
            >{{ "deleteTeamWarning" | i18n: collectionName }}</span
          >
        </div>
        <app-gloss-button
          [options]="deleteTeamButton"
          [fontClass]="'tw-font-semibold tw-text-sm'"
        ></app-gloss-button>
      </div>
    </div>
  }
</div>
